import LoaderOverlay from "@edaz/edazkit/LoaderOverlay";
import type { ViewComponent } from "@frui.ts/views/dist";
import Vehicle from "entities/vehicleResponse";
import { observer } from "mobx-react-lite";
import React from "react";
import { formatDateTime, formatLicensePlate } from "utils/localizations";
import type BulkExemptionsViewModel from "viewModels/callCenter/actions/bulkExemptionsViewModel";
import Tooltip from "@edaz/edazkit/Tooltip";
import type VehicleRegisterVehicleClassifiedFuelType from "entities/vehicleRegisterVehicleClassifiedFuelType";
import { czechDate } from "utils/dateUtils";
import { emptyValue } from "utils/emptyValue";

// eslint-disable-next-line sonarjs/cognitive-complexity
const BulkExemptionsTableView: ViewComponent<BulkExemptionsViewModel> = observer(({ vm }) => {
  if (!vm.previewResponse?.notableItems) {
    return null;
  }
  function generateErrors(items: string[]) {
    return items.map(item => {
      return (
        <>
          <strong className="text-danger">{vm.translateCreationError(item.toString())}</strong>
        </>
      );
    });
  }

  function generateWarnings(items: string[], fuelType?: VehicleRegisterVehicleClassifiedFuelType) {
    return items.map(item => {
      return (
        <>
          <strong>
            {vm
              .translateWarningReason(item.toString())
              .replace("%fuel_type%", fuelType ? vm.translateClassifiedFuelType(fuelType) : "")}
          </strong>
        </>
      );
    });
  }
  function generateTooltip(newDate: Date, originalDate?: any) {
    const validSinceChangeTooltip = vm
      .translate("validSinceChanged")
      .replace("%originalDate%", originalDate ? originalDate : vm.translate("valueEmpty"))
      .replace("%newDate%", newDate ? formatDateTime(newDate) : vm.translate("valueEmpty"));

    return (
      <Tooltip id="license-plate-tooltip" className="license-plate-tooltip" tooltip={validSinceChangeTooltip ?? ""}>
        <span className="font-weight-bold text-secondary link-underline text-wrap">
          <span className="text-nowrap">{formatDateTime(newDate)}</span>
        </span>
      </Tooltip>
    );
  }
  function parseCustomDate(str: any) {
    if (!str || typeof str !== "string" || !str.includes(".")) {
      return null;
    }

    const [datePart, timePart = "00:00:00"] = str.split(" ");
    const [day, month, year] = datePart.split(".");
    if (!day || !month || !year) {
      return null;
    }

    const isoString = `${year}-${month}-${day}T${timePart}`;
    const date = new Date(isoString);

    return isNaN(date.getTime()) ? null : date;
  }

  function isSameDay(dateStr1: any, dateStr2: any) {
    const date1 = parseCustomDate(dateStr1);
    const date2 = parseCustomDate(dateStr2);

    if (!date1 || !date2) {
      return false;
    }

    date1.setHours(0, 0, 0, 0);
    date2.setHours(0, 0, 0, 0);

    return date1.getTime() === date2.getTime();
  }
  return (
    <>
      <LoaderOverlay loading={vm.busyWatcher.isBusy}>
        <table className="table table-tbody-striped tbody-larger">
          <thead>
            <tr>
              <th className="text-nowrap">{vm.translatePreviewAttribute("rowNumber")}</th>
              <th className="text-nowrap">{vm.translatePreviewAttribute("licensePlate")}</th>
              <th className="text-nowrap">{vm.translatePreviewAttribute("validSince")}</th>
              <th className="text-nowrap">{vm.translatePreviewAttribute("validUntil")}</th>
              <th>{vm.translatePreviewAttribute("errors")}</th>
            </tr>
          </thead>
          <>
            {vm.creationItems?.map((item, indexItem) => {
              const fuelType = item.vehicleClassifiedFuelType;
              const vehicle =
                item.licensePlate && item.countryId
                  ? formatLicensePlate(new Vehicle(item.licensePlate, item.countryId))
                  : `${item.originalCountryCode} - ${item.originalLicensePlate}`;

              const validUntil = item.validUntil ? formatDateTime(czechDate(new Date(item.validUntil))) : emptyValue;
              const validSince = item.validSince ? formatDateTime(czechDate(new Date(item.validSince))) : emptyValue;

              const originalValidSince = item.originalValidSince;

              const showTooltipValidSince =
                !isSameDay(validSince, originalValidSince) || (item.validSince && !originalValidSince);
              const tooltipValidSince =
                showTooltipValidSince && item.validSince
                  ? generateTooltip(new Date(item.validSince), originalValidSince)
                  : undefined;

              return (
                <>
                  <tbody>
                    <tr key={`${item.rowNumber + indexItem}`} className={`${(indexItem + 1) % 2 === 0 ? "even" : "odd"}`}>
                      <td className="text-nowrap">{item.rowNumber}</td>
                      <td className="text-nowrap">{vehicle}</td>
                      <td className="text-nowrap">{showTooltipValidSince ? tooltipValidSince : validSince ?? emptyValue}</td>
                      <td className="text-nowrap">{validUntil ?? emptyValue}</td>
                      <td style={{ display: item.errors.length > 1 || item.warningReasons.length > 1 ? "grid" : "table-cell" }}>
                        {generateErrors(item.errors)}
                        {generateWarnings(item.warningReasons, fuelType)}
                      </td>
                    </tr>
                  </tbody>
                </>
              );
            })}
          </>
        </table>
      </LoaderOverlay>
    </>
  );
});

export default BulkExemptionsTableView;
